import React, { useCallback, useMemo, useState } from 'react';
import { isUndefined } from 'lodash';
import { toast } from 'react-toastify';

import * as Core from '../../../core';
import { cs2MatchGameNeedsDeferredMapSetup } from './cs2DeferredMapSetup';
import { Button } from '../../../components/button';
import InfoMessage from '../../../components/infoMessage';
import { CopyTextField } from '../../../components/inputs';
import IntegrationService from '../../../services/integrationService';

import './serverConnect.scss';

interface Cs2ServerConnectProps {
    gameFeatureSet: Core.Models.FeatureSet;
    matchGame?: Core.Models.MatchGame;
    matchState: Core.Models.MatchState;
}

const Cs2ServerConnect = (props: Cs2ServerConnectProps): JSX.Element => {
    const { gameFeatureSet, matchGame, matchState } = props;

    const [isRegenerating, setIsRegenerating] = useState<boolean>(false);

    if (gameFeatureSet !== Core.Models.FeatureSet.CounterStrike2) return <></>;
    if (matchState !== Core.Models.MatchState.InProgress) return <></>;
    if (isUndefined(matchGame)) return <></>;

    let connectString: string | null = null;
    let password: string | null = null;

    if (matchGame?.titleMetadata) {
        try {
            const metadata = JSON.parse(matchGame.titleMetadata);
            connectString = metadata.connect || null;
            password = metadata.password || null;
        } catch {
            // Ignore parse errors
        }
    }

    const awaitingMapSelection = useMemo(
        () => cs2MatchGameNeedsDeferredMapSetup(matchGame),
        [matchGame]
    );

    const regenerateServer = useCallback(
        async (matchGameId: string) => {
            if (isRegenerating) return;

            setIsRegenerating(true);

            try {
                await IntegrationService.regenerateCs2Server({ matchGameId });
                toast.success('Successfully submitted request to regenerate CS2 server.');
            } catch (e) {
                const message = Core.API.getErrorMessage(e);
                toast.error(`Unable to regenerate CS2 server: ${message}`);
            } finally {
                setIsRegenerating(false);
            }
        },
        [isRegenerating]
    );

    if (!connectString) {
        return (
            <div className="cs2-server-connect p2x">
                <form className="form">
                    <div className="generate-server">
                        {awaitingMapSelection ? (
                            <InfoMessage
                                message="Choose a map for this game above. Connection details will appear here after the server is created."
                                type="info"
                            />
                        ) : (
                            <>
                                <InfoMessage message="Error generating CS2 server" type="error" />
                                {!!matchGame && (
                                    <Button disabled={isRegenerating} onClick={() => regenerateServer(matchGame.id)}>
                                        Retry server setup
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="cs2-server-connect p2x">
            <form className="form">
                <p className="info">
                    <strong>Connect to the CS2 server using the information below.</strong>
                </p>
                <p className="info">
                    Open CS2 console (press ~) and paste: <code>connect {connectString}</code>
                    {password && ` (Password: ${password})`}
                </p>
                <p className="info">
                    Scores should be automatically reported at the end of a match. If you don't see scores reported
                    within 5 minutes after a match has completed, click Score Details and then Reprocess.
                </p>
                <fieldset className="form-group">
                    <CopyTextField
                        id="serverConnect"
                        label={`Game ${matchGame.sortOrder} connection`}
                        value={`connect ${connectString}`}
                    />
                    {password && (
                        <CopyTextField
                            id="serverPassword"
                            label="Server password"
                            value={password}
                        />
                    )}
                </fieldset>
            </form>
        </div>
    );
};

export default Cs2ServerConnect;
